import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import { Helmet } from "react-helmet-async"
import {
  fetchUserAccounts,
  selectUserAccountsByContext,
  selectUserAccountsByIds,
} from "./slice"
import { parseUserAccountFilters } from "./filters"
import { updateSearchParamsWithFilters } from "../filters/filters"
import Layout from "../../ui/Layout"
import NavMain from "../../app/NavMain"
import ScrollablePage from "../../ui/ScrollablePage"
import Paper from "../../ui/Paper"
import ErrorMsg from "../../ui/ErrorMsg"
import LoadingSpinner, { Backdrop } from "../../ui/LoadingSpinner"
import { Action, Actions, Header, Overview } from "../../ui/PageHeader"
import LoadingSpinnerInline from "../../ui/LoadingSpinnerInline"
import Button from "../../ui/Button"
import { faPlus } from "@fortawesome/free-solid-svg-icons"
import UserAccountsTable from "./UserAccountsTable"
import CreateUserAccount from "./CreateUserAccount"

const PAGE_SIZE = 1000 // Default initial and subsequent page size

/* UserAccountsList renders a page containing a filter- and sort-able table
 * of all user accounts matching the criteria in the current URL.  */
const UserAccountsList = () => {
  const dispatch = useDispatch()

  const [isAddingUserAccount, setIsAddingUserAccount] = useState(false)

  // we get any matching accounts
  const accountMatches = useSelector((state) =>
    selectUserAccountsByContext(state, "root")
  )
  // We get the full objects from the ids that match
  const accounts = useSelector((state) =>
    selectUserAccountsByIds(state, accountMatches?.ids || [])
  )

  // Parse parameters into filter object (which drives filters UI state)
  // Note: We are using the URL as the "state" here, and updates
  // perform a history replacement (so as not to mess up "back" button
  // behaviour too much - though we may want to apply a heuristic like
  // small changes replace history, big changes push history)
  const location = useLocation()
  const navigate = useNavigate()
  const urlParams = new URLSearchParams(location.search)
  const filter = parseUserAccountFilters(urlParams)
  updateSearchParamsWithFilters(urlParams, filter)

  useEffect(() => {
    dispatch(
      fetchUserAccounts({
        filter,
        page: { offset: 0, limit: PAGE_SIZE },
        context: ["root"],
      })
    )
  }, [urlParams.toString()])

  // How we apply a filter update. Filter state is entirely in the URL query parameters
  const setFilter = (filter = {}) => {
    const updUrlParams = updateSearchParamsWithFilters(urlParams, filter)
    navigate({
      pathname: location.pathname,
      search: `?${updUrlParams}`,
    })
  }

  // How many more are available to load?
  const loadableCount = Math.min(
    PAGE_SIZE,
    accounts?.length && accountMatches?.page?.total_count
      ? accountMatches.page.total_count - accounts.length
      : 50
  )
  // Event handler for 'Show xxx more' button that was clicked
  const handleLoadMore = () => {
    const lastIndex = accounts?.length

    const page = {
      offset: lastIndex,
      limit: loadableCount,
    }
    dispatch(fetchUserAccounts({ filter, page, context: ["root"] }))
  }

  const handleAddUserAccount = () => {
    setIsAddingUserAccount(!isAddingUserAccount)
  }

  const handleCloseAddUserAccount = () => {
    setIsAddingUserAccount(false)
  }

  // Close on ESC
  useEffect(() => {
    const handler = (e) => {
      if (e.key == "Escape") {
        setIsAddingUserAccount(false)
      }
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  })

  return (
    <Layout>
      <NavMain key="nav-left" />
      <Helmet>
        <title>User accounts - Astrosite</title>
      </Helmet>
      <ScrollablePage>
        {accounts.length === 0 && accountMatches?._loadStatus === "loading" && (
          <Backdrop>
            <LoadingSpinner />
          </Backdrop>
        )}
        <Header>
          <Overview>
            <h1>User Accounts</h1>
            <p>
              {accounts.length > 0 && (
                <span>
                  {accountMatches?._loadStatus === "loading" && (
                    <LoadingSpinnerInline />
                  )}
                  Showing <strong>{accountMatches?.page?.end_index}</strong>
                  {accountMatches?.page?.total_count && (
                    <span>
                      {" "}
                      of{" "}
                      <strong className="count total_count">
                        {accountMatches.page.total_count}
                      </strong>
                    </span>
                  )}{" "}
                  accounts
                </span>
              )}
            </p>
          </Overview>
          <Actions>
            <>
              <Action>
                <Button onClick={handleAddUserAccount} icon={faPlus}>
                  Add user account
                </Button>
              </Action>
            </>
          </Actions>
        </Header>
        {accountMatches?._loadStatus == "rejected" && (
          <ErrorMsg
            msg={"There was a problem fetching user accounts"}
            showIcon={true}
          />
        )}
        <Paper>
          <UserAccountsTable
            accounts={accounts}
            filter={filter}
            setFilter={setFilter}
            flagLastAccessOlderThan="P14D"
          />
          {accounts?.length == 0 && accountMatches?._loadStatus == "idle" && (
            <p>There are no user accounts matching your criteria.</p>
          )}
          <footer className="actions">
            {accounts.length > 0 &&
              accounts.length !== accountMatches?.page?.total_count && (
                <>
                  <button
                    onClick={handleLoadMore}
                    disabled={accountMatches?._loadStatus == "loading"}
                  >
                    {accountMatches?._loadStatus == "loading"
                      ? "Loading..."
                      : `Show ${loadableCount} more`}
                  </button>
                </>
              )}
          </footer>
        </Paper>
      </ScrollablePage>
      {isAddingUserAccount && (
        <CreateUserAccount handleCancel={handleCloseAddUserAccount} />
      )}
    </Layout>
  )
}

export default UserAccountsList
